import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { Search } from 'lucide-react';
import { slotTeamIds, type Competition, type ForecastEntry, type Match, type Tournament } from '@airmail/engine';
import { Input, SectionHead, cx } from '@/ui';
import { courtLabel, partnerIn, slotLabel, teamLookup } from '@/lib/teams';
import { fmtTime } from '@/lib/format';
import { useT } from '@/i18n';

/**
 * „Wann spielt wer?" (docs/17 §2) — je Team die nächste Partie mit voraussichtlicher
 * Zeit und Feld. Suchfeld für den Spieler, der am Tisch steht und fragt. Läuft ohne
 * Turnier-Kontext, damit auch die Zuschauer-Ansicht (docs/17 §9) sie zeigen kann.
 */
export function WhoPlaysWhen({
  tournament,
  comp,
  forecast,
  venueFilter,
  courtIds,
  highlight,
  onTeam,
}: {
  tournament: Tournament;
  comp: Competition;
  forecast: Map<string, ForecastEntry>;
  venueFilter?: (m: Match) => boolean;
  courtIds?: Set<string>;
  /**
   * „Mein Team" in der Zuschauer-Ansicht (docs/17 §9). Daniel: „kann man dann
   * in den upcoming games und tabelle das farblich markieren, das man seine
   * sachen schnell findet?" — Die Karte wird blau und steht **ganz oben**:
   * Ohne Suche zeigt die Liste nur die nächsten zwölf, und bei 40 Teams wäre
   * das eigene sonst gar nicht dabei. Farbe allein hätte da nicht gereicht.
   */
  highlight?: string;
  /**
   * Klick auf ein Team: auf der Spiele-Seite schlägt das seine Partien unter
   * „Beendet" auf (Harald, 10/2026). Ohne Handler bleiben die Karten reine Anzeige.
   */
  onTeam?: (teamId: string) => void;
}) {
  const t = useT();
  const lookup = useMemo(() => teamLookup(comp), [comp]);
  const matches = useMemo(() => comp.phases.flatMap((p) => p.matches), [comp]);
  const [q, setQ] = useState('');
  const [openAll, setOpenAll] = useState(false);

  // Sprungziel `#wer` (von der Handy-Einstiegsseite, docs/notes/start.md):
  // Der Abschnitt steht weit unten auf „Spiele“ — nach dem Klick hierher scrollen.
  const box = useRef<HTMLElement>(null);
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash.endsWith('#wer')) return;
    box.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [hash]);

  type Row = { teamId: string; opponent: string; when: string; court?: string; running: boolean; startsAt: number };
  const rows = useMemo(() => {
    const byTeam = new Map<string, Row>();
    // Wechselnde Partner: „C / D (mit B)“
    const gegner = (other: Match['a'], m: Match, id: string) => {
      const partner = partnerIn(m, id, lookup);
      const g = slotLabel(other, lookup, matches, true);
      return partner ? `${g} (${t('mit {p}', { p: partner.shortName })})` : g;
    };
    // laufende Spiele zuerst: „spielt gerade"
    for (const m of matches) {
      if (m.status !== 'running' || !m.courtId || (courtIds && !courtIds.has(m.courtId))) continue;
      for (const [side, other] of [
        [m.a, m.b],
        [m.b, m.a],
      ] as const) {
        for (const id of slotTeamIds(side)) {
          if (byTeam.has(id)) continue;
          byTeam.set(id, {
            teamId: id,
            opponent: gegner(other, m, id),
            when: t('spielt gerade'),
            court: courtLabel(tournament, m.courtId),
            running: true,
            startsAt: 0,
          });
        }
      }
    }
    const upcoming = [...forecast.values()].sort((a, b) => a.startsAt - b.startsAt);
    for (const f of upcoming) {
      const m = matches.find((x) => x.id === f.matchId);
      if (!m || (venueFilter && !venueFilter(m))) continue;
      for (const [side, other] of [
        [m.a, m.b],
        [m.b, m.a],
      ] as const) {
        for (const id of slotTeamIds(side)) {
          if (byTeam.has(id)) continue;
          byTeam.set(id, {
            teamId: id,
            opponent: gegner(other, m, id),
            when: `~${fmtTime(f.startsAt)}`,
            court: courtLabel(tournament, f.courtId),
            running: false,
            startsAt: f.startsAt,
          });
        }
      }
    }
    return [...byTeam.values()].sort(
      (a, b) => Number(b.teamId === highlight) - Number(a.teamId === highlight) || a.startsAt - b.startsAt || (lookup(a.teamId)?.name ?? '').localeCompare(lookup(b.teamId)?.name ?? ''),
    );
  }, [matches, forecast, tournament, venueFilter, courtIds, lookup, highlight, t]);

  const needle = q.trim().toLowerCase();
  const hit = (teamId: string) => {
    if (!needle) return true;
    const team = lookup(teamId);
    if (!team) return false;
    return [team.name, team.club ?? '', ...team.players.flatMap((p) => [p.firstName, p.lastName])].some((s) => s.toLowerCase().includes(needle));
  };
  const shown = rows.filter((r) => hit(r.teamId));
  const limit = needle || openAll ? shown.length : 12;

  return (
    <section id="wer" ref={box} className="mt-8 scroll-mt-16">
      <SectionHead title={t('Wann spielt wer?')} meta={rows.length ? t('{n} Teams mit nächster Partie · Zeiten sind geschätzt', { n: rows.length }) : undefined}>
        <label className="flex items-center gap-1.5">
          <Search size={13} className="text-dim" />
          <Input className="w-56 max-w-full py-1 text-sm" placeholder={t('Team, Spieler oder Mannschaft')} value={q} onChange={(e) => setQ(e.target.value)} aria-label={t('Team suchen')} />
        </label>
      </SectionHead>
      {rows.length === 0 ? (
        <div className="text-sm text-dim">{t('Keine offenen Spiele.')}</div>
      ) : shown.length === 0 ? (
        <div className="text-sm text-dim">{t('Kein Team passt zu „{q}“.', { q })}</div>
      ) : (
        <>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(min(300px,100%),1fr))] gap-1.5">
            {shown.slice(0, limit).map((r) => {
              const team = lookup(r.teamId);
              const mein = r.teamId === highlight;
              return (
                // Nur *eine* bg-Klasse: Bei `bg-panel2 bg-blue/10` entscheidet die Reihenfolge im Stylesheet, nicht die im Attribut — die Karte bliebe grau
                <div
                  key={r.teamId}
                  data-team={r.teamId}
                  className={cx('border px-3 py-2 flex items-center gap-3 text-sm', mein ? 'border-blue bg-blue/10' : 'border-line bg-panel2', onTeam && 'cursor-pointer hover:border-muted')}
                  {...(onTeam
                    ? {
                        role: 'button',
                        tabIndex: 0,
                        title: t('Alle Partien von {team}', { team: team?.name ?? '' }),
                        onClick: () => onTeam(r.teamId),
                        onKeyDown: (e: React.KeyboardEvent) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onTeam(r.teamId);
                          }
                        },
                      }
                    : {})}
                >
                  <div className="min-w-0 flex-1">
                    <div className={cx('font-bold tracking-tight truncate', mein && 'text-blue')}>
                      {team?.name ?? '?'}
                      {mein && <span className="label text-blue ml-2">{t('Mein Team')}</span>}
                    </div>
                    {/* Gegner genauso groß wie das Team — Harald: „der 2. Mannschaftsname wird nur klein angezeigt" */}
                    <div className="truncate">
                      <span className="text-dim">{t('gegen')}</span> <b className="tracking-tight">{r.opponent}</b>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className={cx('font-mono text-[13px]', r.running ? 'text-live' : 'text-ink')}>{r.when}</div>
                    {r.court && <div className="text-[11px] text-dim">{r.court}</div>}
                  </div>
                </div>
              );
            })}
          </div>
          {shown.length > limit && (
            <button className="label mt-2 cursor-pointer hover:text-ink" onClick={() => setOpenAll(true)} data-wer-alle>
              {t('alle {n} zeigen', { n: shown.length })}
            </button>
          )}
          {/*
            Zurück zur kurzen Liste (Harald, 10/2026): Aufgeklappt schiebt sie
            „Beendet" weit nach unten. Danach dorthin scrollen, wo die Liste
            beginnt — sonst steht man mitten in der nächsten Sektion.
          */}
          {openAll && !needle && shown.length > 12 && (
            <button
              className="label mt-2 cursor-pointer hover:text-ink"
              data-wer-weniger
              onClick={() => {
                setOpenAll(false);
                box.current?.scrollIntoView({ block: 'start' });
              }}
            >
              {t('weniger zeigen')}
            </button>
          )}
        </>
      )}
    </section>
  );
}
